import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Dialog, DialogContent } from '@material-ui/core'
import { Close } from '@material-ui/icons'
import { Image, Spinner } from 'ui'
import {
  Button, TextField, Checkbox,
  Table, TableBody, TableCell, TableRow,
} from '@material-ui/core'
import MekomeTablePagination from 'components/MekomeTablePagination'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import useStore from 'store'
import * as XLSX from 'xlsx'

import {
  DialogHeader,
  Header,
  CategoryListTitleContainer,
  CategoryListTitleSubContainer1,
  CategoryListTitleSubContainer2,
  HeaderTitle1,
  HeaderTitle2,
  ImageHeader,
  StyledIconButton,
  NewCategoryContainerActions,
} from './CategoryModal.styles'

import {
  SearchBar,
  SearchBarContainer,
  SearchInput,
  HeaderButton,
  HeaderImageIcon,
  TableContainerStyled,
  TableHeadStyled,
  TableCellTitle,
} from './Protocols.styles'

import User4Icon from 'assets/icons/ims/User4.svg'
import SearchIcon from 'assets/icons/Search.svg'

// ── local styled ──────────────────────────────────────────────────────────────

const SaveButton = styled(Button)`
  padding: 8.724px 23.631px 7.261px 23.992px;
  border-radius: 8px;
  background: #155DFC;
  color: #FFF;
  font-family: Rubik;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
`
const CancelButton = styled(Button)`
  padding: 9.634px 23.98px 8.17px 24.901px;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  background: #FFF;
  color: #364153;
  font-family: Rubik;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
`
const ToolbarRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`
const StatChips = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`
const StatChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid #BFDBFE;
  background: #EFF6FF;
  color: #1447E6;
  font-family: Rubik;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
`
const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`
const HeadCell = styled(TableCell)`
  background: rgba(241, 245, 249, 0.95) !important;
  color: #4C5564 !important;
  font-family: Rubik !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  line-height: 20px !important;
`
const StyledEmailInput = styled(TextField)`
  width: 100%;
  .MuiOutlinedInput-root {
    border-radius: 6px;
    background: #F8FAFC;
    font-family: Rubik;
    font-size: 14px;
  }
  .MuiOutlinedInput-input {
    padding: 6px 10px;
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: #E2E8F0;
  }
`

// ── helpers ────────────────────────────────────────────────────────────────────

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

// ── types ──────────────────────────────────────────────────────────────────────

type ManagerRow = {
  userId: number
  fullName: string
  israeliId: number
  protocolEmail: string
  originalEmail: string
  selected: boolean
  originalSelected: boolean
  emailError: boolean
}

// ── component ──────────────────────────────────────────────────────────────────

const SetMainManagerModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { t } = useTranslation('protocols')
  const { protocolsStore, orgs } = useStore()
  const [rows, setRows] = useState<ManagerRow[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return

    const init = async () => {
      if (protocolsStore.baseUrl === '') {
        await protocolsStore.init(orgs.currentOrgId)
      }

      setLoading(true)
      const users = await protocolsStore.loadMainManagers()
      setRows(
        users.map(u => ({
          userId: u.userId,
          fullName: u.fullName,
          israeliId: u.israeliId,
          protocolEmail: u.protocolEmail ?? '',
          originalEmail: u.protocolEmail ?? '',
          selected: u.isMainManager ?? false,
          originalSelected: u.isMainManager ?? false,
          emailError: false,
        }))
      )
      setLoading(false)
    }
    init()
  }, [open])

  const toggleSelected = (userId: number) => {
    setRows(prev =>
      prev.map(r => {
        if (r.userId !== userId) return r
        const selected = !r.selected
        const emailChanged = r.protocolEmail.trim() !== r.originalEmail.trim()
        const emailError = selected
          ? !!r.protocolEmail && !isValidEmail(r.protocolEmail)
          : emailChanged && r.protocolEmail.length > 0 && !isValidEmail(r.protocolEmail)
        return { ...r, selected, emailError }
      })
    )
  }

  const setEmail = (userId: number, email: string) => {
    setRows(prev =>
      prev.map(r => {
        if (r.userId !== userId) return r
        const emailChanged = email.trim() !== r.originalEmail.trim()
        const emailError = r.selected
          ? email.length > 0 && !isValidEmail(email)
          : emailChanged && email.length > 0 && !isValidEmail(email)
        return { ...r, protocolEmail: email, emailError }
      })
    )
  }

  const filteredRows = rows.filter(r => {
    const q = search.trim().toLowerCase()
    if (!q) return true
    return r.fullName.toLowerCase().includes(q) || String(r.israeliId).includes(q)
  })
  const pagedRows = filteredRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage)

  const isEmailChanged = (r: ManagerRow) => r.protocolEmail.trim() !== r.originalEmail.trim()

  const selectedCount = rows.filter(r => r.selected).length
  const emailCount = rows.filter(r => r.selected && r.protocolEmail.trim()).length
  const hasSelected = rows.some(r => r.selected)
  const hasErrors = rows.some(r => (r.selected || isEmailChanged(r)) && r.emailError)

  const handleSave = async () => {
    const validated = rows.map(r => {
      const trimmedEmail = r.protocolEmail.trim()
      const emailError = r.selected
        ? !isValidEmail(trimmedEmail)
        : isEmailChanged(r) && trimmedEmail.length > 0 && !isValidEmail(trimmedEmail)
      return { ...r, emailError }
    })
    setRows(validated)
    if (validated.some(r => r.emailError)) return

    setSaving(true)
    const entries = validated
      .filter(r => r.selected || isEmailChanged(r) || r.selected !== r.originalSelected)
      .map(r => ({ isMainManager: r.selected, userId: r.userId, protocolEmail: r.protocolEmail.trim() }))
    const ok = await protocolsStore.saveMainManagers(entries)
    setSaving(false)
    if (ok) onClose()
  }

  // The spreadsheet column names stay Hebrew whatever the interface language
  // is: they are the file's format, and export has to match what import reads.
  const handleExport = () => {
    const data = rows.map(r => ({
      'שם משתמש': r.fullName,
      'ת"ז': r.israeliId,
      'כתובת דוא"ל לפרוטוקול': r.protocolEmail,
      'מנהל ראשי': r.selected ? 'כן' : 'לא',
    }))
    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'מנהלים ראשיים')
    XLSX.writeFile(wb, 'main-managers.xlsx')
  }

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      const data = new Uint8Array(ev.target?.result as ArrayBuffer)
      const wb = XLSX.read(data, { type: 'array' })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const jsonRows: any[] = XLSX.utils.sheet_to_json(ws)
      setRows(prev =>
        prev.map(r => {
          const match = jsonRows.find(j => String(j['ת"ז']) === String(r.israeliId))
          if (!match) return r
          const email = String(match['כתובת דוא"ל לפרוטוקול'] ?? '').trim()
          const isMain = match['מנהל ראשי'] === 'כן'
          return { ...r, protocolEmail: email, selected: isMain || !!email, emailError: false }
        })
      )
    }
    reader.readAsArrayBuffer(file)
    e.target.value = ''
  }

  return (
    <Dialog style={{ zIndex: 1200 }} open={open} fullWidth maxWidth="md" scroll="paper">
      <DialogHeader>
        <Header>
          <CategoryListTitleContainer>
            <CategoryListTitleSubContainer2>
              <ImageHeader src={User4Icon} />
            </CategoryListTitleSubContainer2>
            <CategoryListTitleSubContainer1>
              <HeaderTitle1>{t`main_manager_title`}</HeaderTitle1>
              <HeaderTitle2>{t`main_manager_subtitle`}</HeaderTitle2>
            </CategoryListTitleSubContainer1>
          </CategoryListTitleContainer>
          <StyledIconButton onClick={onClose}>
            <Close style={{ color: '#364153' }} />
          </StyledIconButton>
        </Header>
      </DialogHeader>

      <DialogContent style={{ padding: 0 }}>
        <div style={{ padding: '16px 24px 0 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

          <ToolbarRow>
            <StatChips>
              <StatChip>{t('main_manager_selected', { count: selectedCount })}</StatChip>
              <StatChip>{t('main_manager_emails', { count: emailCount })}</StatChip>
              <StatChip>{t('main_manager_users', { count: rows.length })}</StatChip>
            </StatChips>
            {/*<ActionButtons>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleImportFile}
              />
              <HeaderButton
                color={'#0F172A'}
                bg={'#F1F5F9'}
                bcolor={'#E2E8F0'}
                onClick={() => fileInputRef.current?.click()}
              >
                {t`import_from_excel`}
              </HeaderButton>
              <HeaderButton
                color={'#0F172A'}
                bg={'#F1F5F9'}
                bcolor={'#E2E8F0'}
                onClick={handleExport}
              >
                {t`export_to_excel`}
              </HeaderButton>
            </ActionButtons>*/}
          </ToolbarRow>

          <SearchBar style={{ marginTop: 0 }}>
            <SearchBarContainer>
              <SearchInput
                placeholder={t`main_manager_search`}
                variant="outlined"
                InputProps={{ startAdornment: <Image src={SearchIcon} /> }}
                onChange={e => { setPage(0); setSearch(e.target.value) }}
              />
            </SearchBarContainer>
          </SearchBar>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <Spinner />
            </div>
          ) : (
            <TableContainerStyled>
              <Table stickyHeader>
                <TableHeadStyled>
                  <TableRow>
                    <HeadCell style={{ width: 40, padding: '0 4px' }} />
                    <HeadCell>{t`col_user_name`}</HeadCell>
                    <HeadCell>{t`col_israeli_id`}</HeadCell>
                    <HeadCell>{t`col_protocol_email`}</HeadCell>
                  </TableRow>
                </TableHeadStyled>
                <TableBody>
                  {pagedRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" style={{ padding: 32, color: '#4C5564', fontFamily: 'Rubik' }}>
                        {t`no_users_found`}
                      </TableCell>
                    </TableRow>
                  )}
                  {pagedRows.map(row => (
                    <TableRow key={row.userId} hover>
                      <TableCell style={{ width: 40, padding: '0 4px' }}>
                        <Checkbox
                          checked={row.selected}
                          onChange={() => toggleSelected(row.userId)}
                          style={{ padding: 4, color: '#155DFC' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TableCellTitle>{row.fullName}</TableCellTitle>
                      </TableCell>
                      <TableCell>
                        <TableCellTitle>{row.israeliId || '—'}</TableCellTitle>
                      </TableCell>
                      <TableCell style={{ minWidth: 200 }}>
                        <StyledEmailInput
                          variant="outlined"
                          size="small"
                          value={row.protocolEmail}
                          onChange={e => setEmail(row.userId, e.target.value)}
                          placeholder={t`email_placeholder`}
                          disabled={false}
                          error={row.emailError}
                          helperText={row.emailError ? t`email_invalid` : ''}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainerStyled>
          )}

          {!loading && filteredRows.length > 0 && (
            <MekomeTablePagination
              component="div"
              count={filteredRows.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => {
                setRowsPerPage(parseInt(e.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          )}
        </div>
      </DialogContent>

      <NewCategoryContainerActions>
        <CancelButton onClick={onClose}>{t`cancel`}</CancelButton>
        <SaveButton onClick={handleSave} disabled={saving || (!hasSelected && !rows.some(isEmailChanged)) || hasErrors}>
          {saving ? <Spinner size={20} /> : t`save`}
        </SaveButton>
      </NewCategoryContainerActions>
    </Dialog>
  )
}

export default observer(SetMainManagerModal)
